import Box from "@/components/box/box";
import { FullScreenSpinner } from "@/components/fullScreenSpinner/FullScreenSpinner";
import { MfaMethodItem } from "@/components/mfaItem/mfaMethodItem";
import { translate } from "@/config/translator";
import {
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogActions,
  DialogTitle,
  DialogContent,
  Switch,
} from "@fluentui/react-components";
import { faClock, faShieldXmark } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useMfaSettings } from "./mfaSettings/useMfaSettings";
import ConfirmationDialog from "@/components/confirmationDialog/ConfirmationDialog";
import PspTextInput from "@/components/pspTextInput/pspTextInput";
import PspButton from "@/components/pspButton/PspButton";

export function MfaSettings() {
  const {
    lang,
    user,
    mfaMethods,
    methodPopoverOpenId,
    setMethodPopoverOpenId,
    wantsToEditMethod,
    wantsToDeleteMethod,
    setWantsToDeleteMethod,
    onDeleteMfaMethod,
    tempLabel,
    setTempLabel,
    startEditingMethod,
    stopEditingMethod,
    tempActive,
    tempPrimary,
    setTempActive,
    setTempPrimary,
    onSaveEdit,
    somethingChanged,
    setMfaSetupState,
    isTablet,
    hasFreshToken,
    navigateToLogin,
    startMfaSetup,
    restoreFocusTargetAttribute,
  } = useMfaSettings();

  if (user)
    return (
      <>
        {!isTablet && (
          <h2 className="font-semibold text-lg">
            {translate("twoFactorAuth", lang)}
          </h2>
        )}
        <Box
          showBottomMessage
          bottomMessage={
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            `${translate("version", lang)}: ${__VERSION__}` as any
          }
          title={translate("twoFactorAuth", lang)}
          topRight={
            hasFreshToken &&
            user.mfaEnabled && (
              <Button
                {...restoreFocusTargetAttribute}
                onClick={() => setMfaSetupState("createNew")}
                appearance="primary"
              >
                {translate("addMethod", lang)}
              </Button>
            )
          }
        >
          {user.mfaEnabled && hasFreshToken ? (
            <>
              {mfaMethods ? (
                <div className="flex flex-col gap-4">
                  {mfaMethods.map((method) => (
                    <MfaMethodItem
                      // primary is hidden for now
                      // primary={method.isPrimary}
                      popoverOpen={methodPopoverOpenId === method.id}
                      onChangePopoverOpen={(open) => {
                        if (open) {
                          setMethodPopoverOpenId(method.id);
                        } else {
                          setMethodPopoverOpenId(null);
                        }
                      }}
                      key={method.id}
                      active={method.isActive}
                      popoverChildren={
                        <div className="flex flex-col gap-2">
                          <h3 className="font-bold text-lg">
                            {translate("action", lang)}
                          </h3>
                          <PspButton
                            appearance="primary"
                            onClick={() => startEditingMethod(method)}
                          >
                            {translate("edit", lang)}
                          </PspButton>
                          <PspButton
                            appearance="secondary"
                            onClick={() => {
                              setMethodPopoverOpenId(null);
                              setWantsToDeleteMethod(method);
                            }}
                          >
                            {translate("delete", lang)}
                          </PspButton>
                        </div>
                      }
                      label={
                        <div className="flex flex-row justify-between items-center">
                          <div className="flex flex-col items-start">
                            <div>
                              {`${
                                method.mfaType === "totp"
                                  ? translate("authenticatorApp", lang)
                                  : translate("email", lang)
                              } - ${method.label}`}
                            </div>
                          </div>
                        </div>
                      }
                      type={method.mfaType}
                    />
                  ))}
                </div>
              ) : (
                <FullScreenSpinner />
              )}
            </>
          ) : (
            <div
              className={clsx(
                "rounded-sm",
                "border",
                "border-neutral",
                "flex",
                "flex-row",
                "items-center",
                "justify-between",
                "p-3",
              )}
            >
              <div className="flex flex-row gap-3 items-center mr-3">
                <div
                  className={clsx(
                    "p-1",
                    "bg-neutral-200",
                    "aspect-square",
                    "rounded-full",
                    "justify-center",
                    "items-center",
                    "flex",
                    "size-8",
                  )}
                >
                  <FontAwesomeIcon
                    className="text-font-secondary text-xl aspect-square"
                    icon={hasFreshToken ? faShieldXmark : faClock}
                  />
                </div>
                <p>
                  <span>
                    {translate(
                      hasFreshToken ? "twoFactorAuthIs" : "yourTokenIsNotFresh",
                      lang,
                    )}
                  </span>
                  {hasFreshToken && (
                    <span className="text-warning">
                      {" " + translate("notEnabledLowercaseFeminine", lang)}
                    </span>
                  )}
                </p>
                {hasFreshToken && (
                  <p className="text-font-secondary">
                    {translate("enable2faForExtraSecurity", lang)}
                  </p>
                )}
              </div>
              <div>
                <Button
                  onClick={hasFreshToken ? startMfaSetup : navigateToLogin}
                  className="text-nowrap"
                  appearance="primary"
                >
                  {translate(
                    hasFreshToken ? "setup2FaShort" : "goToLogin",
                    lang,
                  )}
                </Button>
              </div>
            </div>
          )}
        </Box>

        {/* DELETE */}
        <ConfirmationDialog
          isOpen={!!wantsToDeleteMethod}
          messageString={translate("youAreAboutToDelete2faMethod", lang, {
            "{methodLabel}": wantsToDeleteMethod?.label ?? "",
          })}
          onCancel={() => setWantsToDeleteMethod(null)}
          onConfirm={onDeleteMfaMethod}
          title="areYouSure"
        />

        {/* EDIT */}
        {wantsToEditMethod && (
          <Dialog open={!!wantsToEditMethod}>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>{translate("editMfaMethod", lang)}</DialogTitle>

                <DialogContent>
                  <div className="flex flex-col gap-4">
                    {tempLabel !== undefined && (
                      <PspTextInput
                        label="label"
                        onChange={setTempLabel}
                        value={tempLabel}
                      />
                    )}

                    <div className="flex flex-row gap-12 items-center mb-6">
                      {tempActive !== undefined && (
                        <Switch
                          checked={tempActive}
                          onChange={(_, data) => setTempActive(data.checked)}
                          label={translate("active", lang)}
                        />
                      )}

                      {/* primary is hidden for now
                        tempPrimary !== undefined && (
                        <Switch
                          checked={tempPrimary}
                          onChange={(_, data) => setTempPrimary(data.checked)}
                          label={translate("primaryMethod", lang)}
                        />
                      )*/}
                    </div>
                  </div>
                </DialogContent>

                <DialogActions>
                  <PspButton onClick={stopEditingMethod} appearance="secondary">
                    {translate("cancel", lang)}
                  </PspButton>
                  <PspButton
                    disabled={!somethingChanged}
                    onClick={onSaveEdit}
                    appearance="primary"
                  >
                    {translate("save", lang)}
                  </PspButton>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        )}
        {!isTablet && (
          <PspButton
            className="mb-8"
            onClick={() => setMfaSetupState("createNew")}
            appearance="secondary"
          >
            {translate("addMethod", lang)}
          </PspButton>
        )}
      </>
    );

  return null;
}
